// === DOORLOPENDE REIS SCREEN ===
// Doorlopende reisverzekering (continuous travel insurance) product screen.
//
// DELIBERATELY THIN. Unlike the older product screens (Inboedel ~8.8k lines,
// Woonhuis ~7.4k, Personenauto ~5.1k), this screen is being built config-driven
// from the start: the sections/fields/coverages will be described in a product
// config (sourced from products/doorlopendereis.json) and rendered by small,
// reusable section components — NOT hand-written inline JSX. Keep this file lean;
// when content arrives it goes into the config + shared components, not here.

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useWorkflow,
  useSharedAddress,
  useConfig,
  WorkflowPageLayout,
  getNextScreen,
  getPreviousScreen,
  getCurrentStepNumber,
  calculateTotalSteps,
  bffClient,
} from '@sluitstraat/core';
import { commonStyles } from '../../styles/commonStyles';
import { WorkflowNavButtons } from '../../components/WorkflowNavButtons';
import { AddressSection, Address } from '../../components/AddressSection';
import { PersonalInfoSection } from '../../components/PersonalInfoSection';
import { DebugPanel } from '../../components/DebugPanel';
import { CoverageOptionsCard } from '../../components/CoverageOptionsCard';
import { WithHelpSidebar } from '../../components/HelpCard';
import { shouldHideAddressSection } from '../../utils/addressSharing';
import { findUpstreamFamilyComposition, findSharedFamilyComposition, syncFamilyCompositionToOtherProducts } from '../../utils/familyComposition';
import { useSharedFamilyComposition } from '../../hooks/useSharedFamilyComposition';
import { buildDfdRequest, DFD_CALC_ENDPOINT, extractPremiums, extractEngineMessages, DfdPremiums, VpAddress } from './doorlopendereis/calc';
import {
  DOORLOPENDE_REIS_DEFAULTS,
  DOORLOPENDE_REIS_REQUIRED_FIELDS,
  SelectOption,
  FAMILY_COMPOSITION_FIELD,
} from './doorlopendereis/fields';

const SCREEN_ID = 'doorlopendereis';

export const DoorlopendeReisScreen: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { getStepData, saveStepData, selectedProducts, relationData } = useWorkflow();
  const { address: sharedAddress } = useSharedAddress();
  const { config } = useConfig();
  const appName = (config as any)?.branding?.appName || (config as any)?.customer?.name || 'SAA Verzekeringen';

  const calcDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  const calcController = useRef<AbortController | null>(null);

  const currentStep = getCurrentStepNumber(`/workflow/${SCREEN_ID}`, selectedProducts);
  const totalSteps = calculateTotalSteps(selectedProducts);

  // An earlier selected product (e.g. Aansprakelijkheid) owns the address; this
  // screen then consumes the shared store instead of rendering its own section.
  const hideAddress = shouldHideAddressSection('doorlopendereis', selectedProducts, getStepData);

  // First step into the insurance: collect the core inputs into a flat `values`
  // map (keyed by AFD field code). A later step maps this into the proper
  // Contractdocument structure and fires the BFF recalc (see fields.ts /
  // ui-logic.md). System defaults are seeded once on entry.
  const [values, setValues] = useState<Record<string, string | number>>(() => {
    const saved = getStepData(SCREEN_ID)?.values;
    const base = { ...DOORLOPENDE_REIS_DEFAULTS, ...(saved || {}) };
    // Seed gezinssamenstelling from an earlier product synchronously, so the first calc already has
    // it even though the field is hidden (it is reused, not re-asked). The effect below keeps it in
    // sync if the upstream value arrives/changes later.
    if (!base.OD_GSS) {
      // Prefill from any value the user already picked on any product (incl. a deselected one),
      // not just an earlier selected product — the value is shared, entered once.
      const shared = findSharedFamilyComposition(getStepData);
      if (shared) base.OD_GSS = shared;
    }
    return base;
  });

  // The debounced calc must read the LATEST values/address. getStepData is backed by
  // React state (setData), so it lags by a render — the closure inside a debounced
  // doCalculate would otherwise send the pre-change value (e.g. old Beroepsmilitair).
  // These refs are written synchronously on every change so the very next calc is correct.
  const valuesRef = useRef(values);

  const [premiums, setPremiums] = useState<DfdPremiums | null>(() => {
    const stored = getStepData(SCREEN_ID)?.Contractdocument;
    return stored?.PP ? extractPremiums({ output: { Contractdocument: stored } }) : null;
  });
  const [isCalculating, setIsCalculating] = useState(false);
  const [calcInput, setCalcInput] = useState<any>(null);
  const [calcOutput, setCalcOutput] = useState<any>(null);
  const [calcErrors, setCalcErrors] = useState<string[]>([]);
  const [calcFailed, setCalcFailed] = useState(false);

  useEffect(() => {
    if (!selectedProducts?.doorlopendereis) {
      navigate('/workflow/product-selection', { replace: true });
      return;
    }
    const clientInfo = relationData?.clientInfo;
    if (!clientInfo || !clientInfo.email || !clientInfo.initials || !clientInfo.lastName) {
      navigate('/workflow/client-information', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    saveStepData(SCREEN_ID, { ...getStepData(SCREEN_ID), values });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const vpFromStep = (): VpAddress => {
    const vp = getStepData(SCREEN_ID)?.Relatiedocument?.VP || {};
    return {
      VP_PCODE: vp.VP_PCODE,
      VP_HUISNR: vp.VP_HUISNR,
      VP_TOEVOEG: vp.VP_TOEVOEG,
      VP_STRAAT: vp.VP_STRAAT,
      VP_PLAATS: vp.VP_PLAATS,
    };
  };

  const vpRef = useRef<VpAddress>(vpFromStep());

  // Recalc the premium the same way the other product screens do, but debounced so
  // a burst of changes (mount + address resolve + field edits) collapses into ONE
  // POST instead of spamming productservice/calculate (which trips the circuit breaker).
  // Read the latest values + address from step data at fire time (setField persists them
  // synchronously) so a stale closure can never drop a just-made selection like Gezinssamenstelling.
  const doCalculate = async () => {
    const vals = valuesRef.current;
    const vp = vpRef.current;
    if (!vp.VP_STRAAT || !vp.VP_PLAATS) return; // wait until the address resolves
    // Hold off the calc until every mandatory user input is filled — no premium on an
    // incomplete risk. Clear any stale premium so the UI doesn't show an old amount.
    const missingRequired = DOORLOPENDE_REIS_REQUIRED_FIELDS.some((k) => {
      const v = vals[k];
      return v == null || v === '';
    });
    if (missingRequired) {
      setPremiums(null);
      setCalcErrors([]);
      setCalcFailed(false);
      return;
    }
    calcController.current?.abort();
    const ctrl = new AbortController();
    calcController.current = ctrl;
    const request = buildDfdRequest(vals, vp, appName);
    setCalcInput(request);
    setIsCalculating(true);
    try {
      const res = await bffClient.request<any>('POST', DFD_CALC_ENDPOINT, request, ctrl.signal);
      setCalcOutput(res);
      const prem = extractPremiums(res);
      const errs = extractEngineMessages(res);
      const priced = (prem.display ?? 0) > 0;
      setPremiums(priced ? prem : null);
      setCalcErrors(errs);
      setCalcFailed(!priced && errs.length === 0);
      const drContract = res?.output?.Contractdocument;
      if (drContract && priced && errs.length === 0) {
        saveStepData(SCREEN_ID, {
          ...getStepData(SCREEN_ID),
          values: vals,
          Relatiedocument: { VP: vpRef.current },
          Contractdocument: drContract,
        });
      }
    } catch (e: any) {
      if (ctrl.signal.aborted) return; // superseded by a newer calc
      setCalcOutput({ error: e instanceof Error ? e.message : 'Calculation failed' });
      setPremiums(null);
      setCalcErrors([]);
      setCalcFailed(true);
    } finally {
      if (calcController.current === ctrl) setIsCalculating(false);
    }
  };

  const recalc = () => {
    if (calcDebounce.current) clearTimeout(calcDebounce.current);
    calcDebounce.current = setTimeout(() => void doCalculate(), 400);
  };

  const setField = (key: string, value: string | number) => {
    const next = { ...valuesRef.current, [key]: value };
    valuesRef.current = next; // sync: the next (debounced) calc reads this immediately
    setValues(next);
    saveStepData(SCREEN_ID, { ...getStepData(SCREEN_ID), values: next });
    // Family composition is picked once and shared with every other selected product.
    if (key === 'OD_GSS' && typeof value === 'string' && value) {
      syncFamilyCompositionToOtherProducts(SCREEN_ID, value, selectedProducts, getStepData, saveStepData);
    }
    recalc();
  };

  const resolveOptions = (opts: SelectOption[]) =>
    opts.map((o) => ({ value: o.value, label: o.labelKey ? t(o.labelKey, o.label) : o.label }));


  const upstreamGss = findUpstreamFamilyComposition(getStepData, SCREEN_ID, selectedProducts);
  const hideFamilyComposition = !!upstreamGss;

  // Single source of truth for the family composition across every product.
  const { familyComposition, setFamilyComposition } = useSharedFamilyComposition();

  // Migrate a value entered before the shared store existed / on a since-deselected product.
  useEffect(() => {
    if (!familyComposition) {
      const found = findSharedFamilyComposition(getStepData);
      if (found) setFamilyComposition(found);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [familyComposition]);

  // Mirror the shared value into this screen's OD_GSS so the calc/payload carries it.
  useEffect(() => {
    if (!familyComposition || valuesRef.current['OD_GSS'] === familyComposition) return;
    const next = { ...valuesRef.current, OD_GSS: familyComposition };
    valuesRef.current = next;
    setValues(next);
    saveStepData(SCREEN_ID, { ...getStepData(SCREEN_ID), values: next });
    recalc();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [familyComposition]);

  useEffect(() => {
    if (upstreamGss && valuesRef.current['OD_GSS'] !== upstreamGss) {
      setField('OD_GSS', upstreamGss);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- setField is a per-render function; the effect is keyed on upstreamGss and guards on the current value
  }, [upstreamGss]);

  // Policyholder address (no risk property for travel) — persisted as Relatiedocument.VP
  // fields, mapped when the payload is built later.
  const initialAddress: Partial<Address> | undefined = (() => {
    const vp = getStepData(SCREEN_ID)?.Relatiedocument?.VP;
    if (!vp) return undefined;
    return {
      postcode: vp.VP_PCODE || '',
      houseNumber: vp.VP_HUISNR ? String(vp.VP_HUISNR) : '',
      addition: vp.VP_TOEVOEG || '',
      street: vp.VP_STRAAT || '',
      city: vp.VP_PLAATS || '',
    };
  })();

  const saveVp = (vp: Record<string, string>) => {
    vpRef.current = {
      VP_PCODE: vp.VP_PCODE,
      VP_HUISNR: vp.VP_HUISNR,
      VP_TOEVOEG: vp.VP_TOEVOEG,
      VP_STRAAT: vp.VP_STRAAT,
      VP_PLAATS: vp.VP_PLAATS,
    };
    saveStepData(SCREEN_ID, { ...getStepData(SCREEN_ID), Relatiedocument: { VP: vp } });
  };

  const handleAddressChange = (a: Address) => {
    const vp = {
      VP_PCODE: a.postcode,
      VP_HUISNR: a.houseNumber,
      VP_TOEVOEG: a.addition,
      VP_STRAAT: a.street,
      VP_PLAATS: a.city,
    };
    saveVp(vp);
    recalc();
  };

  useEffect(() => {
    if (!premiums) recalc();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!sharedAddress?.BA_PCODE) return;
    const next = {
      VP_PCODE: sharedAddress.BA_PCODE,
      VP_HUISNR: String(sharedAddress.BA_HUISNR ?? ''),
      VP_TOEVOEG: sharedAddress.BA_TOEVOEG || '',
      VP_STRAAT: sharedAddress.BA_STRAAT || '',
      VP_PLAATS: sharedAddress.BA_PLAATS || '',
    };
    const cur = vpRef.current;
    if (cur.VP_PCODE === next.VP_PCODE
      && String(cur.VP_HUISNR ?? '') === next.VP_HUISNR
      && (cur.VP_TOEVOEG || '') === next.VP_TOEVOEG
      && (cur.VP_STRAAT || '') === next.VP_STRAAT
      && (cur.VP_PLAATS || '') === next.VP_PLAATS) return;
    saveVp(next);
    recalc();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sharedAddress]);

  // Navigation resolves through the workflow step config; once this product is
  // added to SAA_WORKFLOW_STEPS the helpers return the real neighbours. Until
  // then Back falls back to product selection and Next is a no-op.
  const handleBack = () => {
    const prevScreen = getPreviousScreen(`/workflow/${SCREEN_ID}`, selectedProducts);
    navigate(prevScreen || '/workflow/product-selection');
  };

  const handleNext = () => {
    // Carry the latest calculated Contractdocument forward (fallback in case a debounce was
    // mid-flight) so the summary always has DR's priced quote.
    const drContract = calcOutput?.output?.Contractdocument;
    saveStepData(SCREEN_ID, {
      ...getStepData(SCREEN_ID),
      values,
      ...(drContract ? { Contractdocument: drContract } : {}),
      completed: true,
    });
    const nextScreen = getNextScreen(`/workflow/${SCREEN_ID}`, selectedProducts);
    if (nextScreen) navigate(nextScreen);
  };

  return (
    <WorkflowPageLayout currentStep={currentStep} totalSteps={totalSteps}>
      <main style={commonStyles.page}>
        <div style={commonStyles.container}>
          {/* Full-container header + product badge above the grid — same structure as
              the other product screens (Personenauto is the reference). */}
          <WithHelpSidebar stackEarly>
            <header style={commonStyles.contentHeader}>
              <h1 style={commonStyles.title}>{t('products.doorlopendereis.name')}</h1>
            </header>

            {/* formColumn = shared 900px content width (same source as Aansprakelijkheid). */}
            <div style={commonStyles.formColumn}>
            {!hideAddress && <AddressSection value={initialAddress} onChange={handleAddressChange} />}

            <PersonalInfoSection
              value={familyComposition || String(values['OD_GSS'] ?? '')}
              onChange={(v) => { setField('OD_GSS', v); setFamilyComposition(v); }}
              options={resolveOptions(FAMILY_COMPOSITION_FIELD.options)}
              hideFamilyField={hideFamilyComposition}
            />

            {/* Dekkingsopties — standardized coverage-options display (shared component).
                The Ongevallen cover is folded into DR (no separate sums), so this product
                has one always-included main coverage. */}
            <CoverageOptionsCard
              title={t('screens.doorlopendereis.sections.coverageOptions', 'Dekking & premie')}
              totalLabel={t('common.total', 'Totaal')}
              total={premiums?.display ?? null}
              calculating={isCalculating}
              items={[
                {
                  label: t('products.doorlopendereis.name', 'Doorlopende reisverzekering'),
                  descriptions: [1, 2, 3, 4, 5, 6, 7].map((n) => t(`screens.doorlopendereis.coverage.point${n}`)),
                  amount: premiums?.display ?? null,
                },
              ]}
            />
            {calcErrors.map((e) => (
              <p key={e} style={commonStyles.fieldErrorMessage}>{e}</p>
            ))}
            {calcFailed && !isCalculating && (
              <p style={commonStyles.fieldErrorMessage}>
                {t('common.premiumCalculationError', 'De premie kan op dit moment niet worden berekend. Probeer het later opnieuw of neem contact met ons op.')}
              </p>
            )}

            <DebugPanel
              input={calcInput}
              output={calcOutput}
              onRecalculate={() => recalc()}
              calculating={isCalculating}
            />

            </div>

            <WorkflowNavButtons onBack={handleBack} onNext={handleNext} nextDisabled={calcErrors.length > 0 || calcFailed} />
          </WithHelpSidebar>

        </div>
      </main>
    </WorkflowPageLayout>
  );
};

export default DoorlopendeReisScreen;
